import { useState, useEffect, useCallback } from 'react';
import axiosInstance from 'helpers/api';
import toast from 'react-hot-toast';

interface LeadAIContext {
  property_history?: {
    properties_transacted?: Array<{
      address: string;
      transactions: string[];
      investment_type: string;
      roi_timeline: string;
    }>;
    total_value_transacted?: string;
    transaction_count?: number;
    investor_profile?: string;
  };
  buyer_context?: {
    preferences?: any;
    properties_of_interest?: any[];
    search_stage?: string;
    urgency?: string;
  };
  behavioral_insights?: {
    email_engagement?: string;
    response_pattern?: string;
    total_emails?: number;
    last_activity?: string;
  };
  recommendations?: string[];
  vip_status?: {
    is_vip?: boolean;
    transaction_count?: number;
    total_value?: string;
  };
  confidence_score?: number;
  total_emails_analyzed?: number;
  last_email_analysis_date?: string;
}

interface HaikuTaskResponse {
  next_steps: Array<{
    action: string;
    deadline: string;
    priority: number;
  }>;
  recommendations: string[];
  urgency: 'critical' | 'high' | 'medium' | 'low';
}

export const useLeadAIContext = (leadId: string) => {
  const [context, setContext] = useState<LeadAIContext | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<Date | null>(null);
  
  // Fetch AI context from backend
  const fetchAIContext = useCallback(async () => {
    if (!leadId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔄 [AI CONTEXT] Fetching updated context for lead:', leadId);
      
      // First try to get from lead evaluation endpoint which includes AI context
      const response = await axiosInstance.get(`/leads/${leadId}/evaluation`);
      
      if (response.data.success && response.data.evaluation) {
        const evaluation = response.data.evaluation;
        
        // Extract AI context from evaluation response
        if (evaluation.behavioral_data) {
          const aiContext: LeadAIContext = {
            behavioral_insights: evaluation.behavioral_data,
            confidence_score: evaluation.score,
            // Check if we have enhanced data from email analysis
            property_history: evaluation.behavioral_data.property_history,
            buyer_context: evaluation.behavioral_data.buyer_context,
            vip_status: evaluation.behavioral_data.vip_status,
            total_emails_analyzed: evaluation.behavioral_data.total_emails
          };
          
          setContext(aiContext);
          setLastFetch(new Date());
          
          // Show notification if VIP customer - only on manual refresh
          // Removed automatic toast to prevent duplicate notifications
          
          console.log('✅ [AI CONTEXT] Updated with email analysis data:', aiContext);
        }
      }
      
    } catch (err: any) {
      console.error('❌ [AI CONTEXT] Error fetching context:', err);
      setError(err.response?.data?.error || 'Failed to fetch AI context');
    } finally {
      setLoading(false);
    }
  }, [leadId]);
  
  // Fetch Haiku-generated tasks
  const fetchHaikuTasks = useCallback(async (): Promise<HaikuTaskResponse | null> => {
    if (!leadId) return null;
    
    try {
      console.log('🎯 [HAIKU] Fetching AI-generated tasks for lead:', leadId);
      
      const response = await axiosInstance.post('/api/leads/haiku-tasks', {
        lead_id: leadId,
        user_id: localStorage.getItem('user_id') // Get from storage
      });
      
      if (response.data.success) {
        console.log('✅ [HAIKU] Generated tasks:', response.data);
        return response.data;
      }
      
    } catch (err: any) {
      console.error('❌ [HAIKU] Error generating tasks:', err);
    }
    
    return null;
  }, [leadId]);
  
  // Auto-refresh context when lead changes
  useEffect(() => {
    fetchAIContext();
  }, [leadId, fetchAIContext]);
  
  // Refresh context (can be triggered manually)
  const refreshContext = useCallback(async () => {
    await fetchAIContext();
    
    // Show success notification
    toast.success('AI context refreshed', {
      icon: '🔄',
      duration: 2000
    });
  }, [fetchAIContext]);
  
  // Check if context needs refresh (older than 30 minutes)
  const needsRefresh = useCallback(() => {
    if (!lastFetch) return true;
    const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000);
    return lastFetch < thirtyMinutesAgo;
  }, [lastFetch]);
  
  return {
    context,
    loading,
    error,
    refreshContext,
    needsRefresh,
    lastFetch,
    fetchHaikuTasks
  };
};